import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FaEye, FaEdit, FaFileExport, FaQuestionCircle, FaCheck, FaTimes, FaCoffee, FaClipboardList, FaExchangeAlt } from "react-icons/fa";
import AttendanceModal from "./attendance_modal";
import ViewAttendance from "./view_attendance";
import RegularizationModal from "./regularization_modal";
import ViewRegularization from "./view_regularization";
import Dropdown from "../../components/modal/dropdown";
import DatePicker from "../../components/modal/datepicker";
import { parseTimeString } from "../../components/modal/timepicker";
import Modal from "../../components/modal/modal";
import Can from "../common/can";
import Pagination from "../common/pagination";
import { toast } from "react-toastify";
import {
  getAttendance,
  getAttendanceById,
  createAttendance,
  updateAttendance,
  exportAttendanceCSV,
  bulkMarkAttendance,
  getRegularizations,
  createRegularization,
  approveRegularization,
  rejectRegularization,
  // toggleBreak,
} from "../services/attendanceservice";
import {
  AttendanceStatusFilterOptions,
  getAttendanceStatusLabel,
  getAttendanceStatusClass,
  RegularizationStatus,
  RegularizationStatusFilterOptions,
  getRegularizationStatusLabel,
  getRegularizationStatusClass,
} from "../../constants/constants";
import BulkAttendanceModal from "./bulk_attendance_modal";
import { useAuth } from "../context/authcontext";
import "./attendance.css";

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------
// Shared helpers for resolving a regularization row's Department/
// Designation. Exported so view_regularization.jsx can import them
// straight from here instead of from separate util files — this is the
// one place that owns: how to read dept/designation off any shape of
// object, how to match a regularization record to an attendance row, and
// the cache that lets the background prefetch below and the popup's own
// on-demand fallback share results instead of duplicating lookups.
// ---------------------------------------------------------------------

// Pulls a display-ready Department/Designation string off any object that
// might carry them in one of a few shapes (nested object, plain string, or
// a *_name field).
export const formatDesignationOf = (obj) => {
  if (!obj) return null;
  if (typeof obj.designation === "object" && obj.designation) return obj.designation.name || null;
  if (typeof obj.designation === "string" && obj.designation) return obj.designation;
  return obj.designation_name || null;
};

export const formatDepartmentOf = (obj) => {
  if (!obj) return null;
  if (typeof obj.department === "object" && obj.department) return obj.department.name || null;
  if (typeof obj.department === "string" && obj.department) return obj.department;
  return obj.department_name || null;
};

// Given a regularization record and a list of attendance rows, finds the
// attendance row (if any) belonging to the same employee — matched by id
// first, then emp_id, then full name as a last resort. Attendance's own
// employee_detail reliably carries department/designation as plain
// strings, so once a match is found here, that's a trustworthy source for
// those fields without needing a separate employee-detail fetch.
export const matchAttendanceForRegularization = (attendanceRows, regularization) => {
  const r = regularization || {};
  const empDetail = r.employee_detail || {};

  const hit = (attendanceRows || []).find((a) => {
    const aDetail = a.employee_detail || {};
    const targetId = r.employee ?? r.employee_id ?? empDetail.id;
    if (targetId && (a.employee === targetId || aDetail.id === targetId)) return true;
    if (empDetail.emp_id && (aDetail.emp_id === empDetail.emp_id || a.emp_id === empDetail.emp_id)) return true;

    const targetName = r.employee_name || empDetail.full_name;
    const aName = aDetail.full_name || a.employee_name;
    if (targetName && aName) {
      return aName.trim().toLowerCase() === targetName.trim().toLowerCase();
    }
    return false;
  });

  if (!hit) return null;
  return hit.employee_detail || hit;
};

// Module-scoped cache of resolved employee info per regularization id.
// Populated by the background prefetch effect below right after
// regularizations load, so by the time a user opens ViewRegularization for
// a given row, the answer is usually already here and the popup renders
// instantly with no network wait. ViewRegularization also writes to this
// cache from its own on-demand fallback, for the rare row the prefetch
// hasn't gotten to yet.
const regEmployeeCache = new Map();

export const getResolvedRegEmployee = (regId) =>
  regId == null ? null : regEmployeeCache.get(regId) || null;

export const setResolvedRegEmployee = (regId, detail) => {
  if (regId == null || !detail) return;
  regEmployeeCache.set(regId, detail);
};

export const hasResolvedRegEmployee = (regId) =>
  regId == null ? false : regEmployeeCache.has(regId);

// Filter state (statusFilter / regStatusFilter) always holds either the
// sentinel string "all" or a numeric status id — never a display label.
// This one helper is the single place that turns a raw URL/query-string
// value into that canonical shape, so the two never drift apart.
const parseStatusParam = (raw) => {
  if (!raw || raw === "all") return "all";
  const num = Number(raw);
  return Number.isNaN(num) ? "all" : num;
};

// Local (not UTC) "YYYY-MM-DD" for today — matches what the DatePicker's
// maxDate and stored attendance dates use, so a straight string compare
// lines up correctly regardless of the browser's UTC offset.
const todayStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Check-in/Check-out are stored as plain "HH:MM(:SS)" wall-clock time
// (already IST — there's no timezone/date component to convert), so this
// only needs to reformat the digits, not shift any offset. Rendered as
// 12-hour + AM/PM (e.g. "09:05 AM") instead of raw 24-hour "09:05" so it
// reads the same way the rest of the HR-facing UI (shift timings, etc.)
// expects times to look.
const formatTimeDisplay = (t) => {
  const parsed = parseTimeString(t);
  if (!parsed) return "—";
  let hour = parsed.hour % 12;
  if (hour === 0) hour = 12;
  const ampm = parsed.hour >= 12 ? "PM" : "AM";
  return `${String(hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")} ${ampm}`;
};

const Attendance = () => {
  const { hasPermission, hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowAttendanceActions = isAdmin || hasAnyPermission(["attendance.view", "attendance.view_others", "attendance.update", "regularization.request", "attendance.checkin"]);
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("attendance");

  // Attendance State
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch]               = useState("");
  const [statusFilter, setStatusFilter]   = useState(() => {
    const params = new URLSearchParams(location.search);
    return parseStatusParam(params.get("status"));
  });
  // Opening the Attendance Log fresh (e.g. from the sidenav, with no
  // ?date=/?month= in the URL) should land on just today's records —
  // not the whole table. If the link that brought us here already
  // specified a date or a month, honor that instead of overriding it.
  const [dateFilter, setDateFilter]       = useState(() => {
    const params = new URLSearchParams(location.search);
    const paramDate = params.get("date");
    if (paramDate) return paramDate;
    if (params.get("month")) return "";
    return todayStr();
  });
  const [monthFilter, setMonthFilter] = useState(() => {
  const params = new URLSearchParams(location.search);
  return params.get("month") || "";
});
  // Scopes the Attendance Log to a single employee — set from the
  // ?employee= query param, which the Dashboard's "My Attendance This
  // Month" links use (via { mine: true }) so a logged-in user only sees
  // their own records instead of the whole company's. HR/company-wide
  // links never set this param, so it stays empty for them.
  const [employeeFilter, setEmployeeFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("employee") || "";
  });

  const [openModal, setOpenModal]               = useState(false);
  const [openBulkModal, setOpenBulkModal]       = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [viewAttendance, setViewAttendance]     = useState(null);

  // Regularization State
  const [regularizations, setRegularizations] = useState([]);
  const [regLoading, setRegLoading]           = useState(false);
  const [regPage, setRegPage]                 = useState(1);
  const [regTotalCount, setRegTotalCount]     = useState(0);
  const [regStatusFilter, setRegStatusFilter] = useState("all");

  const [openRegModal, setOpenRegModal] = useState(false);
  const [targetAttendance, setTargetAttendance] = useState(null);
  const [viewRegularization, setViewRegularization] = useState(null);

  const [regApprovalModal, setRegApprovalModal] = useState({ show: false, action: "", regId: null, remarks: "" });

  const canManageRegularizations = hasPermission("regularization.approve") || hasPermission("regularization.reject");
  // Actions column (View, plus Approve/Reject when permitted) shows for
  // anyone who can see regularization requests at all, not only approvers —
  // same idea as the Leave table's canShowActions.
  const canShowRegActions =
    canManageRegularizations ||
    hasPermission("regularization.view") ||
    hasPermission("regularization.create");

  const fetchIdRef = useRef(0);
  const regFetchIdRef = useRef(0);

  const fetchAttendance = useCallback(async () => {
    if (!hasPermission("attendance.view")) return;
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      if (dateFilter) params.date = dateFilter;
      if (monthFilter) {
  params.month = monthFilter;
}
      if (employeeFilter) {
        params.employee = employeeFilter;
      }

      const res = await getAttendance(params);
      if (fetchId !== fetchIdRef.current) return;
      const data = res.data || res;
      const meta = res.meta || {};
      setAttendance(Array.isArray(data) ? data : data.results || []);
      setTotalCount(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch {
      if (fetchId === fetchIdRef.current) toast.error("Failed to load attendance");
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [page, search, statusFilter, dateFilter, monthFilter, employeeFilter, hasPermission]);

  const fetchRegularizations = useCallback(async () => {
    if (!hasPermission("regularization.view") && !hasPermission("regularization.create")) return;
    const fetchId = ++regFetchIdRef.current;
    try {
      setRegLoading(true);
      const params = { page: regPage, page_size: PAGE_SIZE };
      if (regStatusFilter !== "all") {
        params.status = regStatusFilter;
      }

      const res = await getRegularizations(params);
      if (fetchId !== regFetchIdRef.current) return;
      const data = res.data || res;
      setRegularizations(Array.isArray(data) ? data : data.results || []);
      setRegTotalCount(data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch {
      if (fetchId === regFetchIdRef.current) toast.error("Failed to load regularizations");
    } finally {
      if (fetchId === regFetchIdRef.current) setRegLoading(false);
    }
  }, [regPage, regStatusFilter, hasPermission]);

  useEffect(() => {
    if (activeTab === "attendance") {
      if (hasPermission("attendance.view")) {
        fetchAttendance();
      } else if (hasPermission("regularization.view") || hasPermission("regularization.create")) {
        setActiveTab("regularizations");
      }
    } else if (activeTab === "regularizations") {
      if (hasPermission("regularization.view") || hasPermission("regularization.create")) {
        fetchRegularizations();
      } else if (hasPermission("attendance.view")) {
        setActiveTab("attendance");
      }
    }
  }, [fetchAttendance, fetchRegularizations, activeTab, hasPermission]);

  useEffect(() => { setPage(1); }, [search, statusFilter, dateFilter, monthFilter, employeeFilter]);
  useEffect(() => { setRegPage(1); }, [regStatusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const date = params.get("date");
    const month = params.get("month");
    const employee = params.get("employee");



    if (status) {
      setActiveTab("attendance");
      setStatusFilter(parseStatusParam(status));
    }
    if (date) {
      setDateFilter(date);
    }
    if (month) {
    setMonthFilter(month);
}
    // Always sync (not just when present) so navigating to a company-wide
    // link afterward correctly clears a previously-set employee scope,
    // instead of leaving a stale "mine" filter applied.
    setEmployeeFilter(employee || "");
  }, [location.search]);

  // Background prefetch: as soon as the regularization list loads, resolve
  // Department/Designation for every row up front — rather than leaving
  // each row's ViewRegularization popup to look it up lazily the moment
  // it's opened, which is what caused the visible lag on rows whose
  // employee wasn't already in the currently-loaded attendance page.
  //
  // Two passes, at most one network call total:
  //  1. Match against the attendance rows already loaded on this page
  //     (covers most rows for free — no request needed).
  //  2. For any rows still unresolved, ONE broad, unscoped attendance
  //     fetch (no date/status filter) to catch employees outside the
  //     currently-loaded window, then match all of them against that
  //     single response — not one request per row.
  useEffect(() => {
    if (!regularizations.length) return;
    let cancelled = false;

    const prefetch = async () => {
      const unresolved = regularizations.filter((r) => {
        if (getResolvedRegEmployee(r.id)) return false;
        const d = r.employee_detail || {};
        const alreadyKnown =
          (formatDepartmentOf(d) && formatDesignationOf(d)) ||
          (formatDepartmentOf(r) && formatDesignationOf(r));
        return !alreadyKnown;
      });
      if (!unresolved.length) return;

      const stillUnresolved = [];
      unresolved.forEach((r) => {
        const hit = matchAttendanceForRegularization(attendance, r);
        if (hit) {
          setResolvedRegEmployee(r.id, hit);
        } else {
          stillUnresolved.push(r);
        }
      });
      if (!stillUnresolved.length || cancelled) return;

      // Grouped by employee identity (emp_id / name / whatever id is on the
      // record) so N regularization requests from the same employee cost
      // one search, not N — and searched with a targeted query per
      // employee instead of one broad page_size sweep, so this scales
      // correctly regardless of how many total regularizations or distinct
      // employees are involved, rather than depending on everyone fitting
      // inside a single fixed-size page.
      const groups = new Map();
      stillUnresolved.forEach((r) => {
        const empDetail = r.employee_detail || {};
        const query = empDetail.emp_id || r.emp_id || r.employee_name || empDetail.full_name;
        if (!query) return;
        const key = query.trim().toLowerCase();
        if (!groups.has(key)) groups.set(key, { query, rows: [] });
        groups.get(key).rows.push(r);
      });

      // Small batches of concurrent requests rather than firing every
      // unique-employee search at once — keeps this gentle on the backend
      // even when a page has many distinct unresolved employees.
      const BATCH_SIZE = 4;
      const entries = Array.from(groups.values());
      for (let i = 0; i < entries.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = entries.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async ({ query, rows }) => {
            try {
              const res = await getAttendance({ search: query, page_size: 5 });
              const data = res.data || res;
              const resultRows = Array.isArray(data) ? data : data.results || [];
              rows.forEach((r) => {
                const hit = matchAttendanceForRegularization(resultRows, r);
                if (hit) setResolvedRegEmployee(r.id, hit);
              });
            } catch {
              // Best-effort prefetch only — ViewRegularization still has
              // its own on-demand fallback for anything left unresolved.
            }
          })
        );
      }
    };

    prefetch();
    return () => {
      cancelled = true;
    };
  }, [regularizations, attendance]);

  const handleSave = async (data) => {
    try {
      if (selectedAttendance) {
        await updateAttendance(selectedAttendance.id, data);
        toast.success("Attendance updated successfully");
      } else {
        await createAttendance(data);
        toast.success("Attendance marked successfully");
      }
      await fetchAttendance();
      setOpenModal(false);
      setSelectedAttendance(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save attendance");
      throw err;
    }
  };

  const handleBulkSave = async (data) => {
    try {
      const res = await bulkMarkAttendance(data);
      toast.success(res?.message || "Bulk attendance saved successfully");
      setOpenBulkModal(false);
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save bulk attendance");
      throw err;
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      if (dateFilter)             params.date   = dateFilter;
      if (monthFilter)            params.month  = monthFilter;
      if (employeeFilter)         params.employee = employeeFilter;
      const res = await exportAttendanceCSV(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started");
    } catch {
      toast.error("Export failed");
    }
  };

  // const handleToggleBreak = async (attendanceId = null) => {
  //   try {
  //     const payload = (typeof attendanceId === "number" || typeof attendanceId === "string") ? { attendance_id: attendanceId } : {};
  //     const res = await toggleBreak(payload);
  //     toast.success(res.message);
  //     fetchAttendance();
  //   } catch (err) {
  //     toast.error(err.response?.data?.message || "Failed to toggle break");
  //   }
  // };

  const handleRegSave = async (data) => {
    try {
      await createRegularization(data);
      toast.success("Regularization requested successfully");
      setOpenRegModal(false);
      setTargetAttendance(null);
      if (activeTab === "regularizations") {
        fetchRegularizations();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit request");
      throw err;
    }
  };

  // Shared by the inline row icons and the ViewRegularization popup footer,
  // so there's exactly one place that opens the approve/reject confirm modal.
  const openRegApproval = (regId, action) => {
    setRegApprovalModal({ show: true, action, regId, remarks: "" });
  };

  const submitRegApproval = async () => {
    const { action, regId, remarks } = regApprovalModal;
    try {
      if (action === "approve") {
        await approveRegularization(regId, { status: RegularizationStatus.APPROVED, remarks: remarks || "Approved" });
        toast.success("Regularization approved");
      } else {
        await rejectRegularization(regId, { status: RegularizationStatus.REJECTED, remarks: remarks || "Rejected" });
        toast.success("Regularization rejected");
      }
      fetchRegularizations();
      setRegApprovalModal({ show: false, action: "", regId: null, remarks: "" });
      setViewRegularization(null);
    } catch {
      toast.error(`Failed to ${action} regularization`);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const regTotalPages = Math.ceil(regTotalCount / PAGE_SIZE);

  return (
    <div className="container">
      <div className="page-header">
        <h2>Attendance Log & Employee Records</h2>
        <div className="attendance-header-actions">
          <Can perm="attendance.export">
            <button className="primary-btn btn-theme-neutral" onClick={handleExport}>
              <FaFileExport /> Export CSV
            </button>
          </Can>
          <Can perm="attendance.create">
            <button className="primary-btn" onClick={() => { setSelectedAttendance(null); setOpenModal(true); }}>
              + Manual Record (HR)
            </button>
          </Can>
          <Can perm="attendance.bulk_mark">
            <button className="primary-btn btn-theme-accent" onClick={() => setOpenBulkModal(true)}>
              ⚡ Bulk Mark Attendance
            </button>
          </Can>
        </div>
      </div>

      <div className="account-tabs">
        {hasPermission("attendance.view") && (
          <button
            className={`account-tab ${activeTab === "attendance" ? "active" : ""}`}
            onClick={() => setActiveTab("attendance")}
          >
            <FaClipboardList /> Attendance Log
          </button>
        )}
        {(hasPermission("regularization.view") || hasPermission("regularization.create")) && (
          <button
            className={`account-tab ${activeTab === "regularizations" ? "active" : ""}`}
            onClick={() => setActiveTab("regularizations")}
          >
            <FaExchangeAlt /> Regularization Requests
          </button>
        )}
      </div>

      {activeTab === "attendance" && (
        <>
          {/* {employeeFilter && (
            <div className="filters-card single" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span className="hr-live-pill">Showing your attendance only</span>
              <button
                type="button"
                className="secondary"
                onClick={() => setEmployeeFilter("")}
              >
                Show all employees
              </button>
            </div>
          )} */}

          <div className="filters-card single" style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="search-input"
              style={{ flex: "2 1 240px" }}
              placeholder="🔍 Search by Name or Emp ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={{ flex: "1 1 180px" }}>
              <Dropdown
                options={AttendanceStatusFilterOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="Status"
              />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <DatePicker
                value={dateFilter}
                onChange={setDateFilter}
                placeholder="Select date"
                maxDate={new Date().toISOString().split("T")[0]}
              />
            </div>
            {dateFilter !== todayStr() && (
              <button
                type="button"
                className="primary-btn"
                style={{ flex: "0 0 auto" }}
                onClick={() => setDateFilter(todayStr())}
              >
                Today
              </button>
            )}
          </div>

          {loading ? (
            <div className="empty-state"><p>Loading attendance…</p></div>
          ) : (
            <table className="table attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Emp ID</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Status</th>
                  {canShowAttendanceActions && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {attendance.length === 0 ? (
                  <tr>
                    <td colSpan={canShowAttendanceActions ? "8" : "7"}>
                      <div className="empty-state">
                        <img src="/images/attendance.svg" alt="No attendance" className="empty-img" />
                        <p>No Attendance Found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  attendance.map((a) => (
                    <tr key={a.id} className="attendance-record-row">
                      <td className="att-date">{a.date}</td>
                      <td className="att-empid">{a.employee_detail?.emp_id || a.emp_id || "—"}</td>
                      <td className="att-name">{a.employee_detail?.full_name || a.employee_name || "—"}</td>
                      <td className="att-department">{a.employee_detail?.department || a.department_name || a.department || "—"}</td>
                      <td className="att-time-range">
                        <span className="att-check-in">{formatTimeDisplay(a.check_in_time)}</span>
                        <span className="att-time-separator"> - </span>
                        <span className="att-check-out">{formatTimeDisplay(a.check_out_time)}</span>
                      </td>
                      <td className="att-checkout-desktop">{formatTimeDisplay(a.check_out_time)}</td>
                      <td className="att-status">
                        <span className={`emp-status ${getAttendanceStatusClass(a.status)}`}>
                          {getAttendanceStatusLabel(a.status)}
                        </span>
                      </td>
                      {canShowAttendanceActions && (
                        <td className="action-icons action-icons-cell att-actions">
                            <button className="icon-btn view" title="View" onClick={async () => {
                              try {
                                const res = await getAttendanceById(a.id);
                                setViewAttendance(res.data || res);
                              } catch (err) { toast.error("Failed to load details"); }
                            }}>
                              <FaEye />
                            </button>
                            <Can perm="attendance.update">
                              <button className="icon-btn edit" title="Edit Record (HR)" onClick={() => { setSelectedAttendance(a); setOpenModal(true); }}>
                                <FaEdit />
                              </button>
                            </Can>
                            <Can perm="regularization.request">
                              <button className="icon-btn" title="Request Regularization" onClick={() => { setTargetAttendance(a); setOpenRegModal(true); }}>
                                <FaQuestionCircle color="#007bff" />
                              </button>
                            </Can>
                            {a.current_break_start && (isAdmin || hasPermission("attendance.view_others")) && (
                              <span className="icon-btn" title="Currently on Break" style={{ cursor: "default" }}>
                                <FaCoffee color="#dc3545" />
                              </span>
                            )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </>
      )}

      {activeTab === "regularizations" && (
        <>
          <div className="filters-card single" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Dropdown
              options={RegularizationStatusFilterOptions}
              value={regStatusFilter}
              onChange={setRegStatusFilter}
              placeholder="Status"
            />
            {(hasPermission("regularization.create") || hasPermission("attendance.view")) && (
              <button className="primary-btn" onClick={() => { setTargetAttendance(null); setOpenRegModal(true); }}>
                + Request Regularization
              </button>
            )}
          </div>

          {regLoading ? (
            <div className="empty-state"><p>Loading regularizations…</p></div>
          ) : (
            <table className="table reg-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Proposed In</th>
                  <th>Proposed Out</th>
                  <th>Proposed Break</th>
                  <th>Reason</th>
                  <th>Status</th>
                  {canShowRegActions && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {regularizations.length === 0 ? (
                  <tr>
                    <td colSpan={canShowRegActions ? 8 : 7}>
                      <div className="empty-state">
                         <img src="/images/regularization.svg" alt="No Regularization" className="empty-img" />
                        <p>No Regularization Requests Found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  regularizations.map((r) => (
                    <tr key={r.id} className="reg-record-row">
                      <td className="reg-date">{r.date}</td>
                      <td className="reg-employee">{r.employee_name || "—"}</td>
                      <td className="reg-in">{formatTimeDisplay(r.requested_check_in)}</td>
                      <td className="reg-out">{formatTimeDisplay(r.requested_check_out)}</td>
                      <td className="reg-break">{r.requested_break_hours ? `${r.requested_break_hours}h` : "—"}</td>
                      <td
                        className="reg-reason"
                        style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {r.reason}
                      </td>

                      <td
                        className="reg-status"
                        style={{ whiteSpace: "nowrap", overflow: "visible", textOverflow: "clip" }}
                      >
                        <span className={`emp-status ${getRegularizationStatusClass(r.status)}`}>
                          {getRegularizationStatusLabel(r.status)}
                        </span>
                      </td>
                      {canShowRegActions && (
                        <td className="action-icons reg-actions">
                            <button
                              className="icon-btn view"
                              title="View"
                              onClick={() => setViewRegularization(r)}
                            >
                              <FaEye />
                            </button>

                            {/* Approve/Reject icons only make sense while the
                                request is still pending. Once it's been
                                approved or rejected, this cell shows just the
                                View button — no disabled tick/cross clutter. */}
                            {r.status === RegularizationStatus.PENDING && (
                              <>
                                <Can perm="regularization.approve">
                                  <button
                                    className="icon-btn view"
                                    title="Approve"
                                    style={{ color: "#28a745" }}
                                    onClick={() => openRegApproval(r.id, "approve")}
                                  >
                                    <FaCheck />
                                  </button>
                                </Can>

                                <Can perm="regularization.reject">
                                  <button
                                    className="icon-btn delete"
                                    title="Reject"
                                    onClick={() => openRegApproval(r.id, "reject")}
                                  >
                                    <FaTimes />
                                  </button>
                                </Can>
                              </>
                            )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {regTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
              <button className="primary-btn" style={{ padding: "6px 12px" }} onClick={() => setRegPage(p => Math.max(1, p - 1))} disabled={regPage === 1}>Prev</button>
              <span style={{ lineHeight: "34px" }}>Page {regPage} of {regTotalPages}</span>
              <button className="primary-btn" style={{ padding: "6px 12px" }} onClick={() => setRegPage(p => Math.min(regTotalPages, p + 1))} disabled={regPage === regTotalPages}>Next</button>
            </div>
          )}
        </>
      )}

      {viewAttendance && (
        <ViewAttendance
          attendance={viewAttendance}
          onClose={() => setViewAttendance(null)}
          onEdit={() => { setSelectedAttendance(viewAttendance); setOpenModal(true); setViewAttendance(null); }}
        />
      )}

      {viewRegularization && (
        <ViewRegularization
          regularization={viewRegularization}
          attendanceRecords={attendance}
          canManage={canManageRegularizations}
          onClose={() => setViewRegularization(null)}
          onApprovalAction={openRegApproval}
        />
      )}

      {openModal && (
        <AttendanceModal
          existing={selectedAttendance}
          onSave={handleSave}
          onClose={() => { setOpenModal(false); setSelectedAttendance(null); }}
        />
      )}

      {openRegModal && (
        <RegularizationModal
          attendanceRecord={targetAttendance}
          attendanceList={attendance}
          onSave={handleRegSave}
          onClose={() => { setOpenRegModal(false); setTargetAttendance(null); }}
        />
      )}

     {regApprovalModal.show && (
  <Modal
    title={regApprovalModal.action === "approve" ? "Approve Regularization" : "Reject Regularization"}
    onClose={() => setRegApprovalModal({ show: false, action: "", regId: null, remarks: "" })}
  >
    <div className="reg-approval-body">
      <div className={`reg-approval-icon ${regApprovalModal.action}`}>
        {regApprovalModal.action === "approve" ? <FaCheck /> : <FaTimes />}
      </div>
      <p className="reg-approval-message">
        Are you sure you want to {regApprovalModal.action} this regularization request? You can provide optional remarks below.
      </p>
      <textarea
        className="reg-approval-textarea"
        placeholder="Manager remarks (optional)"
        value={regApprovalModal.remarks}
        onChange={(e) => setRegApprovalModal({ ...regApprovalModal, remarks: e.target.value })}
      />
    </div>
    <div className="modal-footer">
      <div className="modal-actions">
        <button
          className={`reg-approval-confirm-btn ${regApprovalModal.action}`}
          onClick={submitRegApproval}
        >
          Confirm {regApprovalModal.action === "approve" ? "Approval" : "Rejection"}
        </button>
        <button className="secondary" onClick={() => setRegApprovalModal({ show: false, action: "", regId: null, remarks: "" })}>Cancel</button>
      </div>
    </div>
  </Modal>
)}

      {openBulkModal && (
        <BulkAttendanceModal
          onSave={handleBulkSave}
          onClose={() => setOpenBulkModal(false)}
        />
      )}
    </div>
  );
};

export default Attendance;